import moment from "moment";

// Human-readable dates in the DVSA submission status line (e.g. "15 Jun 2026").
const displayDateFormat = "DD MMM YYYY";

function formatDisplayDate(date) {
    return moment(date).format(displayDateFormat);
}

// The Active line built from the next and last sends, the next first because it is what the operator
// is waiting on. The send date is stated without hedging: a send that slips is reported as delayed.
function buildSendsMessage(lastSend, nextSend) {
    const sentences = [];

    if (nextSend != null) {
        const sendDate = formatDisplayDate(nextSend.sendOnOrAround);

        if (nextSend.isOverdue === true) {
            sentences.push(`Next report to DVSA: due ${sendDate}, delayed.`);
        } else {
            sentences.push(`Next report to DVSA: ${sendDate}.`);
        }
    }

    if (lastSend != null) {
        sentences.push(`Last report to DVSA: ${formatDisplayDate(lastSend.sentDate)}.`);
    }

    return sentences.join(" ");
}

// Builds the DVSA submission status line shown beneath the KPI period selector, from the dashboard
// response's dvsaSubmission block. The block's `state` drives which message (and which sub-object) is
// used; non-applicable sub-objects arrive as null, so each state guards the fields it reads and returns
// null rather than rendering a partial line. `streamLabel` names the KPI stream in the NotSubmitting
// copy ("Drivers" on the driver tab, "Maintenance" on the maintenance tab).
export function buildDvsaSubmissionMessage(dvsaSubmission, streamLabel) {
    if (dvsaSubmission == null) {
        return null;
    }

    if (dvsaSubmission.state === "Active") {
        // A response carrying neither send block predates them, so the line falls back to the single
        // reporting period's send.
        if (dvsaSubmission.lastSend != null || dvsaSubmission.nextSend != null) {
            return buildSendsMessage(dvsaSubmission.lastSend, dvsaSubmission.nextSend);
        }

        // Active carries both the reporting period and the send date; without either there is no
        // meaningful line to show.
        if (dvsaSubmission.reportingPeriod == null || dvsaSubmission.sendOnOrAround == null) {
            return null;
        }

        // The send falls at the start of the active period, so it is in the past for most of that period.
        // A recorded sent date is the only reliable signal that it has happened, so the line follows it.
        if (dvsaSubmission.sentDate != null) {
            return `Last report to DVSA: ${formatDisplayDate(dvsaSubmission.sentDate)}.`;
        }

        return `Next report to DVSA: ${formatDisplayDate(dvsaSubmission.sendOnOrAround)}.`;
    }

    if (dvsaSubmission.state === "NotSubmitting") {
        return `This account is monitoring ${streamLabel} KPIs but is not currently submitting to the DVSA.`;
    }

    if (dvsaSubmission.state === "Pending") {
        // Pending carries the period submissions start from.
        if (dvsaSubmission.fromPeriod == null) {
            return null;
        }

        const fromPeriodText = `DVSA submissions begin from Period ${dvsaSubmission.fromPeriod.isoPeriod}`;

        // Without a parseable scheme start date the line stands on the period alone rather than naming
        // a date it does not have — the same guard the alert-rules modal applies, so the two agree.
        const schemeStart = dvsaSubmission.schemeStartDate == null ? null : moment(dvsaSubmission.schemeStartDate);
        let pendingMessage = `${fromPeriodText}.`;
        if (schemeStart != null && schemeStart.isValid()) {
            pendingMessage = `This account joined the DVSA scheme on ${schemeStart.format(displayDateFormat)}, so ${fromPeriodText}.`;
        }

        const nextSend = dvsaSubmission.nextSend;
        if (nextSend == null) {
            return pendingMessage;
        }

        return `${pendingMessage} First report to DVSA: ${formatDisplayDate(nextSend.sendOnOrAround)}.`;
    }

    return null;
}
